<script>
import Loader from "@/components/widgets/loader.vue";
import moment from 'moment';

export default {
  components: {
    Loader
  },
  props: ['isShow', 'user', 'result', 'aiError', 'loading', 'firstResult', 'latestResult', 'styleFiveMeter', 'styleExport'],
  data() {
    return {
      series: [],
      colorCategoryChart: [],
      checkScore: false,
      styleExportN: this.styleExport ?? false,
    }
  },
  mounted() {
    this.updateSeries();
    this.applyTableStyle();
  },
  watch: {
    result() {
      this.updateSeries();
    },
    firstResult() {
      this.updateSeries();
    },
  },
  methods: {
    updateSeries() {
      const series = [];

      if (this.result?.points) {
        const points = this.result.points;
        series.push({
          name: '今回の結果',
          color: "#298bd0",
          data: [points.speed, points.posture, points.step_width, points.stability, points.rhythm],
        });
      }

      if (this.firstResult?.points) {
        const points = this.firstResult.points;
        series.push({
          name: '初回の結果',
          color: '#E378A1',
          data: [points.speed, points.posture, points.step_width, points.stability, points.rhythm],
        });
      }

      if (this.latestResult?.points) {
        const points = this.latestResult.points;
        series.push({
          name: '前回の結果',
          color: "#F6C95C",
          data: [points.speed, points.posture, points.step_width, points.stability, points.rhythm],
        });
      }

      this.series = series;
      this.checkScore = this.series[0].data.some(point => point < 3);
      this.colorCategoryChart = this.series[0].data.map(point => {
        return point >= 3 ? '#adb5bd' : '#FF0000';
      });

    },
    applyTableStyle() {
      const tableHeaders = document.querySelectorAll('.table thead th');
      const tableCells = document.querySelectorAll('.table tbody td');
      
      if (this.styleExportN) {
        tableHeaders.forEach(header => {
          header.style.boxShadow = 'none';
        });
        tableCells.forEach(cell => {
          cell.style.boxShadow = 'none';
        });
      }
    },
    chartOptions() {
      return {
        chart: {
          height: 370,
          type: 'radar',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: {
          width: 2,
        },
        fill: {
          opacity: 0.6,
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: "vertical",
            shadeIntensity: 0.1,
            gradientToColors: undefined,
            inverseColors: true,
            opacityFrom: 0.8,
            opacityTo: 0.3,
            stops: [0, 90],
          },
        },
        markers: {
          size: 0
        },
        yaxis: {
          show: false,
          stepSize: 1,
        },
        dataLabels: {
          enabled: true,
          background: {
            enabled: true,
            borderRadius: 10,
            padding: 5,
          },
          style: {
            fontSize: '12px',
          },
        },
        plotOptions: {
          radar: {
            polygons: {
              strokeColor: '#999',
              fill: {
                colors: ['#f8f8f8', '#fff']
              }
            }
          }
        },
        xaxis: {
          categories: ['速度', '姿勢', '歩幅左右差', '安定性', 'リズム'],
          labels: {
            style: {
              colors: this.colorCategoryChart,
            }
          }
        }
      };
    }
  }
}
</script>

<template>
  <div v-if="isShow">
    <Loader :loading="loading">
      <div v-if="aiError" class="pt-2 pb-1">
        <div class="text-danger text-center font-weight-semibold">{{ aiError }}</div>
      </div>
      <div>
        <div class="row" style="padding: 0 10px; ">
          <div class="col-12 d-flex justify-content-center align-items-center"
            style="color: white; background-color: #395299; height: 40px;">
            <h3 class="font-weight-semibold" style="margin: 0; color: #ffff;">歩行分析結果</h3>
          </div>
          <div class="col-12 d-flex justify-content-end align-items-end"
            style="color: #ffff; height: 16px; transform: translateY(-20px); font-size: 13px;">
            {{ result?.date }}
          </div>
        </div>
        <div class="row pt-3">
          <div class="col">
            <h5 class="ps-2 font-weight-semibold title02">総合評価</h5>
            <div class="result-sum">
              <span class="font-weight-semibold" style="font-size: 42px; color: #395299">
                {{ result?.sum.toFixed(1) }}
              </span>
              <span style="font-size: 16px;" class="font-weight-semibold"> / 25.0点</span>
            </div>
            <div class="clearfix pt-3">
              <div :class="{ 'custom-float': styleFiveMeter || styleExportN }" class="float-start pt-3">
                <img src="@/assets/images/chat-avatar.png" class="image-item" />
              </div>
              <div class="rounded p-2" style="background: #E2EFF9; margin-left: 80px">
                <span v-if="checkScore" style="font-size: 14px">
                  歩行分析は健康と生活の質を向上させるための第一歩です。体に痛みがあるなど、症状があれば医療従事者に相談してみましょう！
                </span>
                <span v-else style="font-size: 14px">
                  全体的に歩行のバランスがとても良い結果です！この調子で日常生活でも無理せず歩くことを心がけていきましょう！
                </span>
              </div>
            </div>
            <div class="pt-5">
              <h5 class="ps-2 font-weight-semibold title02">歩行バランスチャート</h5>
            </div>
            <div class="text-center pt-3" style="min-width: 300px;">
              <apexchart type="radar" height="370" :options="chartOptions()" :series="series"></apexchart>
            </div>
          </div>

          <div class="col" v-if="!styleExportN">
            <h5 class="ps-2 font-weight-semibold title02">歩行の様子</h5>
            <div class="font-size-13">動画は医療機関で閲覧できます</div>
            <div class="pt-2">
              <video v-if="!styleExportN" class="p-0 m-auto w-100" :src="result?.videos[0]" controls
                style="object-fit: cover; min-height: 600px"></video>
              <img v-else class="p-0 m-auto w-100" style="object-fit: cover; min-height: 600px"
               :src="result?.thumbnails[0]">
            </div>
          </div>
        </div>

        <div class="row pt-4">
          <div class="col-12">
            <h5 class="ps-2 font-weight-semibold title02">歩行情報</h5>
          </div>
          <div class="col-12">
            <div class="pt-2">
              <table class="table">
                <thead>
                  <tr class="text-center">
                    <th>記録</th>
                    <th>測定日</th>
                    <th>速度</th>
                    <th>姿勢</th>
                    <th>リズム</th>
                    <th>安定性</th>
                    <th style="min-width: 70px;">歩幅左右差</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="result?.points" class="text-center font-size-16">
                    <td>今回</td>
                    <td>{{ result.date }}</td>
                    <td>{{ result.points.speed.toFixed(1) }}</td>
                    <td>{{ result.points.posture.toFixed(1) }}</td>
                    <td>{{ result.points.rhythm.toFixed(1) }}</td>
                    <td class="text-danger">{{ result?.points?.stability.toFixed(1) }}</td>
                    <td>{{ result.points.step_width.toFixed(1) }}</td>
                  </tr>
                  <tr v-if="latestResult?.points" class="text-center font-size-14">
                    <td>前回</td>
                    <td>{{ latestResult.date }}</td>
                    <td>{{ latestResult.points?.speed.toFixed(1) }}</td>
                    <td>{{ latestResult.points?.posture.toFixed(1) }}</td>
                    <td>{{ latestResult.points?.rhythm.toFixed(1) }}</td>
                    <td class="text-danger">{{ latestResult.points?.stability.toFixed(1) }}</td>
                    <td>{{ latestResult.points?.step_width.toFixed(1) }}</td>
                  </tr>
                  <tr v-if="firstResult?.points" class="text-center font-size-14">
                    <td>初回</td>
                    <td>{{ firstResult.date }}</td>
                    <td>{{ firstResult.points?.speed.toFixed(1) }}</td>
                    <td>{{ firstResult.points?.posture.toFixed(1) }}</td>
                    <td>{{ firstResult.points?.rhythm.toFixed(1) }}</td>
                    <td class="text-danger">{{ firstResult.points?.stability.toFixed(1) }}</td>
                    <td>{{ firstResult.points?.step_width.toFixed(1) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="row pt-2 pb-4">
          <div class="col-12">
            <h5 class="ps-2 font-weight-semibold title02">評価について</h5>
          </div>
          <div class="d-flex pt-3 col-12 card-container" style="gap: 15px;">
            <div class="card">
              <div class="card-header">
                速度
              </div>
              <div class="card-body">
                <img src="@/assets/images/hokou-ai-1.svg" class="image-item" />
              </div>
              <span class="description" :class="{ 'description-five-meter': styleFiveMeter }">歩行速度</span>
              <div class="card-footer">
                <span class="footer-text">
                  速度が遅くなると、信号が渡りきれないなど日常生活に支障をきたす場合があります。
                </span>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                リズム
              </div>
              <div class="card-body">
                <img src="@/assets/images/hokou-ai-2.svg" class="image-item" style="height: 85px;" :class="{ 'image-item-five-meter-1': styleFiveMeter }" />
              </div>
              <span class="description" :class="{ 'description-five-meter': styleFiveMeter }" >ステップ時間の<br />ばらつき</span>
              <div class="card-footer">
                <span class="footer-text">
                  ばらつきが小さいほど、健康的な歩行パターンと考えられます。
                </span>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                姿勢
              </div>
              <div class="card-body">
                <img src="@/assets/images/hokou-ai-3.svg" class="image-item" style="height: 95px;"/>
              </div>
              <span class="description" :class="{ 'description-five-meter': styleFiveMeter }">体の左右の傾き</span>
              <div class="card-footer">
                <span class="footer-text">
                  傾きが小さいほど、バランスが良く安定した歩行と考えられます。
                </span>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                安定性
              </div>
              <div class="card-body">
                <img src="@/assets/images/hokou-ai-4.svg" class="image-item" :class="{ 'image-item-five-meter-2': styleFiveMeter }"/>
              </div>
              <span class="description" :class="{ 'description-five-meter': styleFiveMeter }">両足が地面に<br>ついている時間</span>
              <div class="card-footer">
                <span class="footer-text">
                  両足接地時間が短いほど、バランスが良く安定した歩行と考えられます。
                </span>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                歩行左右差
              </div>
              <div class="card-body">
                <img src="@/assets/images/hokou-ai-5.svg" class="image-item" />
              </div>
              <span class="description" :class="{ 'description-five-meter': styleFiveMeter }">足の接地時間の<br>左右差</span>
              <div class="card-footer">
                <span class="footer-text">
                  接地時間の左右差が少ないほど、健康的な歩行パターンと考えられます。
                </span>
              </div>
            </div>
          </div>


        </div>


      </div>
    </Loader>
    <div v-if="loading" class="text-center">歩行分析中...</div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css?family=Poppins:300,400,500,600,700&display=swap");

* {
  font-family: 'Poppins', sans-serif !important;
}

@media (min-width: 1200px) {
  .custom-float {
    margin-top: -20px;
  }
}
@media (min-width: 768px) and (max-width: 1024px) {
  .image-item {
    height: 50px;
    width: auto;
  }
  .card-container {
    font-size: 14px !important;
  }
  .description-five-meter {
    font-size: 12px !important;
  }
  .image-item-five-meter-1 {
    height: 65px !important;
  }
  .image-item-five-meter-2 {
    height: 55px !important;
  }
  table thead th,
  table tbody td {
    font-size: 14px !important;
  } 
}


.table {
  border: 2px solid #89D1EA;
  border-radius: 10px;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
}

table thead th,
table tbody td {
  text-align: center;
  padding: 10px;
  box-shadow: inset 0 0 0 0.3px #DBDBDB;
  border: none;
  min-width: 65px;
  white-space: nowrap;
}

table thead th {
  background: #EEF8FE;
  color: #395299;
}

table tbody {
  background: #ffffff;
}

.table {
  overflow: hidden;
}

.result-sum {
  border: 2px solid #89D1EA;
  border-radius: 10px;
  width: 100%;
  text-align: center;
}

.title02 {
  border-left: 3px solid #395299;
  color: #395299;
  font-weight: 600;
}

.card-container {
  display: flex;
  gap: 15px;
}

.card {
  border: 2px solid #89D1EA;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: #FAFDFF;
}

.card-header {
  background-color: #89D1EA;
  color: white;
  width: 100%;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  font-weight: 800;
  white-space: nowrap;
}

.card-body {
  padding: 15px 0;
  height: 90px;
  display: flex;
  justify-content: center;
}

.image-item {
  height: 70px;
  width: auto;
}

.description {
  font-size: 14px;
  color: #395299;
  text-align: center;
  font-weight: 600;
}

.card-footer {
  padding: 7px 8px 5px 8px;
  background-color: #FAFDFF;
  border-radius: 10px;
}

.footer-text {
  font-size: 11px;
}
</style>
